import {appstreamCustomerFromEvent, FP_READY} from "@/lib/appstreamCustomerBridge";
import { getStaffServiceIds } from "@/lib/staff-associations";
import { useState, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Calendar as CalendarIcon, Clock, Euro, User, Users, Mail, Phone, ArrowLeft, ArrowRight, Check, Loader2, ShoppingCart, Trash2, Grid3X3, AlertCircle, Globe, ChevronDown, ChevronRight, Tag, CheckCircle } from "lucide-react";
import LanguageSelector from "@/components/LanguageSelector";
import axios from "axios";
import { toast } from "sonner";
import { format, addMinutes, addDays } from "date-fns";
import { it, enUS, de, es } from "date-fns/locale";
import { API } from "@/App";

// Date-fns locales mapping
const dateLocales = {
  it: it,
  en: enUS,
  de: de,
  es: es
};

// Helper function to format duration nicely with translations
const formatDuration = (minutes, t) => {
  if (minutes >= 1440) {
    const days = Math.floor(minutes / 1440);
    return `${days} ${t ? t('time.days', 'giorni') : (days === 1 ? 'giorno' : 'giorni')}`;
  }
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours} ${t ? t('time.hours', 'ore') : (hours === 1 ? 'ora' : 'ore')}`;
    }
    return `${hours}h ${mins}m`;
  }
  return `${minutes} ${t ? t('time.minutes', 'min') : 'min'}`;
};

const PublicBooking = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const calendarIdFromUrl = searchParams.get("calendar");
  const fcmTokenFromUrl = searchParams.get("fcm_token");  // FCM token from mobile app
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [step, setStep] = useState(1); // Dynamic based on booking_flow_order
  const [loading, setLoading] = useState(true);
  const [business, setBusiness] = useState(null);
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [staff, setStaff] = useState([]);
  const [allStaff, setAllStaff] = useState([]); // Keep all staff for staff_first flow
  const [calendars, setCalendars] = useState([]);
  const [slots, setSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [blockedDates, setBlockedDates] = useState([]);
  
  // Track if staff was pre-selected from URL (calendar with staff_id)
  const [staffPreselectedFromUrl, setStaffPreselectedFromUrl] = useState(false);
  
  // Booking flow order: "service_first" or "staff_first"
  const bookingFlowOrder = business?.booking_flow_order || "service_first";
  
  // Filter by category
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(null);
  
  // Expanded categories (accordion state) - start with first category expanded
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  
  // Toggle category expansion
  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };
  
  // Expanded service descriptions (for "altro" button)
  const [expandedDescriptions, setExpandedDescriptions] = useState(new Set());
  
  // Image zoom state
  const [zoomedImage, setZoomedImage] = useState(null);
  
  const toggleDescription = (serviceId, e) => {
    e.stopPropagation(); // Prevent triggering service selection
    setExpandedDescriptions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(serviceId)) {
        newSet.delete(serviceId);
      } else {
        newSet.add(serviceId);
      }
      return newSet;
    });
  };
  
  // Multiple services selection
  const [selectedServices, setSelectedServices] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [selectedCalendar, setSelectedCalendar] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  
  // Multi-seat and multi-day booking options
  const [numSeats, setNumSeats] = useState(1);
  const [numDays, setNumDays] = useState(1);
  const [endDate, setEndDate] = useState(null);
  
  // Rental date range picker
  const [rentalStartDate, setRentalStartDate] = useState(null);
  const [rentalEndDate, setRentalEndDate] = useState(null);
  
  const [customerInfo, setCustomerInfo] = useState({
    name: "",
    email: "",
    phone: "",
    notes: "",
    num_attendees: 1
  });
  
  useEffect(() => {
    if (window.parent === window || !business?.business_id) return;
    const allowedOrigins = (process.env.REACT_APP_APPSTREAM_ORIGINS || "https://victorious-tranquility-production-df19.up.railway.app").split(",").map(value=>value.trim()).filter(Boolean);
    const receive = event => {
      const customer = appstreamCustomerFromEvent(event, window.parent, allowedOrigins, business.business_id);
      if (customer) setCustomerInfo(previous => ({...previous,...customer}));
    };
    window.addEventListener("message", receive);
    for (const origin of allowedOrigins) window.parent.postMessage({type:FP_READY},origin);
    return () => window.removeEventListener("message",receive);
  }, [business?.business_id]);

  // Recurring booking options
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurringFrequency, setRecurringFrequency] = useState("weekly");
  const [recurringCount, setRecurringCount] = useState(4);

  // Discount code state
  const [discountCode, setDiscountCode] = useState("");
  const [discountResult, setDiscountResult] = useState(null);
  const [validatingDiscount, setValidatingDiscount] = useState(false);

  const validateDiscount = async () => {
    if (!discountCode.trim()) return;
    setValidatingDiscount(true);
    try {
      const serviceIds = selectedServices.map(s => s.service_id);
      const res = await axios.post(`${API}/public/${slug}/validate-discount`, {
        code: discountCode,
        service_ids: serviceIds,
        booking_amount: totalPrice,
        customer_email: customerInfo.email
      });
      setDiscountResult(res.data);
      toast.success(`Codice sconto applicato: -€${res.data.discount_amount.toFixed(2)}`);
    } catch (err) {
      setDiscountResult(null);
      toast.error(err.response?.data?.detail || "Codice sconto non valido");
    }
    setValidatingDiscount(false);
  };

  // Get step labels based on flow order and staff availability
  const getStepLabels = () => {
    const hasStaff = allStaff.length > 0 && allStaff.filter(s => s.is_active !== false).length > 0;
    
    if (!hasStaff) {
      // No staff: show only 3 steps
      return ["Servizi", "Data/Ora", "Conferma"];
    }
    
    // If staff is pre-selected from URL, skip staff selection step
    if (staffPreselectedFromUrl) {
      return ["Servizi", "Data/Ora", "Conferma"];
    }
    
    if (bookingFlowOrder === "staff_first") {
      return ["Operatore", "Servizi", "Data/Ora", "Conferma"];
    }
    return ["Servizi", "Operatore", "Data/Ora", "Conferma"];
  };
  
  // Calculate actual step number for display (accounting for skipped staff step)
  const getDisplayStep = () => {
    const hasStaff = allStaff.length > 0 && allStaff.filter(s => s.is_active !== false).length > 0;
    // If no staff available or staff pre-selected from URL, adjust step
    if ((!hasStaff || staffPreselectedFromUrl) && step > 1) {
      // Adjust step display when staff is skipped
      return step - 1;
    }
    return step;
  };

  // Determine booking type based on selected services
  const getBookingType = () => {
    if (selectedServices.length === 0) return "appointment";
    const firstService = selectedServices[0];
    return firstService.service_type || "appointment";
  };
  
  const bookingType = getBookingType();

  // Calculate totals based on service type
  const calculateTotals = () => {
    if (selectedServices.length === 0) return { duration: 0, price: 0 };
    
    const type = bookingType;
    let totalDuration = 0;
    let totalPrice = 0;
    
    selectedServices.forEach(svc => {
      totalDuration += svc.duration_minutes;
      
      if (type === "event") {
        totalPrice += svc.price * numSeats;
      } else if (type === "daily_rental") {
        totalPrice += (svc.price_per_day || svc.price) * numDays;
      } else {
        totalPrice += svc.price;
      }
    });
    
    return { duration: totalDuration, price: totalPrice };
  };
  
  const { duration: totalDuration, price: totalPrice } = calculateTotals();

  // Group services by category
  const getServicesByCategory = () => {
    const grouped = {};
    
    // First add services with categories
    services.forEach(service => {
      const categoryId = service.category_id || 'uncategorized';
      if (!grouped[categoryId]) {
        const category = categories.find(c => c.category_id === categoryId);
        grouped[categoryId] = {
          category: category || { category_id: 'uncategorized', name: 'Altri Servizi', color: '#6B7280' },
          services: []
        };
      }
      grouped[categoryId].services.push(service);
    });
    
    // Sort: categorized first, then uncategorized
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      if (a === 'uncategorized') return 1;
      if (b === 'uncategorized') return -1;
      return 0;
    });
    
    return sortedKeys.map(key => grouped[key]);
  };

  // Filter services based on selected category
  const getFilteredServices = () => {
    if (!selectedCategoryFilter) {
      return getServicesByCategory();
    }
    const grouped = getServicesByCategory();
    return grouped.filter(g => g.category.category_id === selectedCategoryFilter);
  };

  useEffect(() => {
    fetchBusinessData();
  }, [slug]);

  useEffect(() => {
    // In service_first flow, fetch staff when services are selected
    if (bookingFlowOrder === "service_first" && selectedServices.length > 0) {
      fetchStaff();
    }
  }, [selectedServices, bookingFlowOrder, allStaff]);

  useEffect(() => {
    if (selectedDate && selectedCalendar) {
      fetchSlots();
    }
  }, [selectedDate, selectedCalendar, selectedStaff, totalDuration]);

  const fetchBusinessData = async () => {
    try {
      const [bizResponse, servicesResponse, calendarsResponse, categoriesResponse, staffResponse, blockedResponse] = await Promise.all([
        axios.get(`${API}/public/business/${slug}`),
        axios.get(`${API}/public/business/${slug}/services`),
        axios.get(`${API}/public/business/${slug}/calendars`),
        axios.get(`${API}/public/business/${slug}/categories`),
        axios.get(`${API}/public/business/${slug}/staff`),
        axios.get(`${API}/public/${slug}/blocked-dates`).catch(() => ({ data: [] }))
      ]);
      
      setBusiness(bizResponse.data);
      const allServices = servicesResponse.data;
      const allCalendars = calendarsResponse.data;
      setCalendars(allCalendars);
      const loadedCategories = categoriesResponse.data || [];
      setCategories(loadedCategories);
      setAllStaff(staffResponse.data || []);
      setBlockedDates(blockedResponse.data || []);
      
      // Initialize first category as expanded (if exists)
      if (loadedCategories.length > 0) {
        setExpandedCategories(new Set([loadedCategories[0].category_id]));
      }
      
      // If calendar specified in URL, pre-select it and filter services
      if (calendarIdFromUrl && allCalendars.length > 0) {
        const targetCalendar = allCalendars.find(c => c.calendar_id === calendarIdFromUrl);
        if (targetCalendar) {
          setSelectedCalendar(targetCalendar);
          
          // If calendar has a staff_id, pre-select that staff
          if (targetCalendar.staff_id) {
            const targetStaff = staffResponse.data.find(s => s.staff_id === targetCalendar.staff_id);
            if (targetStaff) {
              setSelectedStaff(targetStaff);
              setStaffPreselectedFromUrl(true); // Mark staff as pre-selected
              // Filter services based on staff's services
              const staffServiceIds = getStaffServiceIds(targetStaff);
              if (staffServiceIds.length > 0) {
                const filteredServices = allServices.filter(s => staffServiceIds.includes(s.service_id));
                setServices(filteredServices);
              } else {
                // Staff has no services assigned - show empty
                setServices([]);
              }
            }
          } else {
            // Filter services to show only those associated with this calendar
            const calendarServiceIds = targetCalendar.service_ids || [];
            if (calendarServiceIds.length > 0) {
              const filteredServices = allServices.filter(s => calendarServiceIds.includes(s.service_id));
              setServices(filteredServices);
            } else {
              // Calendar has no specific services, show all
              setServices(allServices);
            }
          }
        } else {
          setServices(allServices);
        }
      } else {
        setServices(allServices);
      }
      
      // Set language based on business default_language
      if (bizResponse.data.default_language && bizResponse.data.default_language !== i18n.language) {
        i18n.changeLanguage(bizResponse.data.default_language);
      }
      
      // If staff_first flow, show all staff initially
      if (bizResponse.data.booking_flow_order === "staff_first") {
        setStaff(staffResponse.data || []);
      }
    } catch (error) {
      toast.error(t('errors.generic', 'Attività non trovata'));
    } finally {
      setLoading(false);
    }
  };

  const fetchStaff = async () => {
    try {
      // Get staff that can provide at least one of the selected services
      const serviceIds = selectedServices.map(s => s.service_id);
      
      // Filter staff that have at least one of the selected services
      // Staff with no services assigned cannot provide any service
      const filteredStaff = allStaff.filter(s => {
        const staffServiceIds = getStaffServiceIds(s);
        // Staff must have services assigned AND at least one matching the selected services
        return staffServiceIds.length > 0 && staffServiceIds.some(svcId => serviceIds.includes(svcId));
      });
      
      setStaff(filteredStaff);
    } catch (error) {
      console.error("Error fetching staff:", error);
    }
  };

  // Get services filtered by selected staff (for staff_first flow)
  const getServicesForStaff = () => {
    if (!selectedStaff) return services;
    // Get staff services from both 'services' and 'service_ids' fields
    const staffServices = getStaffServiceIds(selectedStaff);
    // If staff has no specific services assigned, they cannot do any services
    if (!staffServices || staffServices.length === 0) {
      return []; // Return empty - staff must have services assigned
    }
    return services.filter(s => staffServices.includes(s.service_id));
  };

  // Get services for staff grouped by category (for staff_first flow)
  const getServicesForStaffByCategory = () => {
    const staffServices = getServicesForStaff().filter(s => s.is_active !== false);
    const grouped = {};
    staffServices.forEach(service => {
      const categoryId = service.category_id || 'uncategorized';
      if (!grouped[categoryId]) {
        const category = categories.find(c => c.category_id === categoryId);
        grouped[categoryId] = {
          category: category || { category_id: 'uncategorized', name: 'Altri Servizi', color: '#6B7280' },
          services: []
        };
      }
      grouped[categoryId].services.push(service);
    });
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      if (a === 'uncategorized') return 1;
      if (b === 'uncategorized') return -1;
      return 0;
    });
    return sortedKeys.map(key => grouped[key]);
  };

  const getFilteredServicesForStaff = () => {
    const all = getServicesForStaffByCategory();
    if (!selectedCategoryFilter) return all;
    return all.filter(g => g.category.category_id === selectedCategoryFilter);
  };

  const fetchSlots = async () => {
    if (!selectedDate || !selectedCalendar) return;
    
    setLoadingSlots(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const serviceIds = selectedServices.map(s => s.service_id).join(",");
      const response = await axios.get(
        `${API}/public/calendar/${selectedCalendar.calendar_id}/slots?date=${dateStr}&service_ids=${serviceIds}&total_duration=${totalDuration}${selectedStaff?.staff_id ? `&staff_id=${encodeURIComponent(selectedStaff.staff_id)}` : ""}`
      );
      setSlots(response.data.slots || []);
    } catch (error) {
      toast.error("Errore nel caricamento degli slot");
      setSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const toggleService = (service) => {
    setSelectedServices(prev => {
      const exists = prev.find(s => s.service_id === service.service_id);
      if (exists) {
        return prev.filter(s => s.service_id !== service.service_id);
      } else {
        return [...prev, service];
      }
    });
    // Reset subsequent selections when services change
    setSelectedSlot(null);
  };

  const removeService = (serviceId) => {
    setSelectedServices(prev => prev.filter(s => s.service_id !== serviceId));
    setSelectedSlot(null);
  };

  const handleContinueToStaff = () => {
    if (selectedServices.length === 0) {
      toast.error("Seleziona almeno un servizio");
      return;
    }
    
    // If staff is pre-selected from URL (calendar with staff_id), skip staff selection
    if (staffPreselectedFromUrl) {
      setStep(3);
      return;
    }
    
    // If no staff available, skip directly to date/time selection (step 3)
    if (allStaff.length === 0 || allStaff.filter(s => s.is_active !== false).length === 0) {
      // No staff, skip to step 3 directly
      setSelectedStaff(null);
      // Only auto-select calendar if not already pre-selected from URL
      if (!selectedCalendar) {
        const serviceIds = selectedServices.map(s => s.service_id);
        const matchingCalendar = calendars.find(c => 
          c.service_ids.some(id => serviceIds.includes(id)) || c.service_ids.length === 0
        );
        if (matchingCalendar) {
          setSelectedCalendar(matchingCalendar);
        } else if (calendars.length > 0) {
          setSelectedCalendar(calendars[0]);
        }
      }
      setStep(3);
      return;
    }
    
    setStep(2);
  };

  const handleStaffSelect = (staffMember) => {
    setSelectedStaff(staffMember);
    // Only auto-select calendar if not already pre-selected from URL
    if (!selectedCalendar || !calendarIdFromUrl) {
      const staffCalendar = calendars.find(c => c.staff_id === staffMember?.staff_id);
      if (staffCalendar) {
        setSelectedCalendar(staffCalendar);
      } else {
        const serviceIds = selectedServices.map(s => s.service_id);
        const serviceCalendar = calendars.find(c => 
          c.service_ids.some(id => serviceIds.includes(id)) || c.service_ids.length === 0
        );
        setSelectedCalendar(serviceCalendar || calendars[0]);
      }
    }
    setStep(3);
  };

  const handleSkipStaff = () => {
    setSelectedStaff(null);
    // Only auto-select calendar if not already pre-selected from URL
    if (!selectedCalendar || !calendarIdFromUrl) {
      const serviceIds = selectedServices.map(s => s.service_id);
      const matchingCalendar = calendars.find(c => 
        c.service_ids.some(id => serviceIds.includes(id)) || c.service_ids.length === 0
      );
      if (matchingCalendar) {
        setSelectedCalendar(matchingCalendar);
      } else if (calendars.length > 0) {
        setSelectedCalendar(calendars[0]);
      }
    }
    setStep(3);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setStep(4);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedServices.length === 0 || !selectedCalendar) {
      toast.error("Seleziona tutti i campi richiesti");
      return;
    }
    
    // For appointments and events, slot is required
    if (bookingType !== "daily_rental" && !selectedSlot) {
      toast.error("Seleziona un orario");
      return;
    }
    
    // For daily rentals, both start and end dates are required
    if (bookingType === "daily_rental") {
      if (!rentalStartDate) {
        toast.error("Seleziona una data di inizio");
        return;
      }
      if (!rentalEndDate) {
        toast.error("Seleziona una data di fine");
        return;
      }
    }

    setSubmitting(true);
    
    try {
      let startDateTimeStr, endDateTimeStr;
      
      if (bookingType === "daily_rental") {
        // For rentals, use rental date range
        const year = rentalStartDate.getFullYear();
        const month = String(rentalStartDate.getMonth() + 1).padStart(2, '0');
        const day = String(rentalStartDate.getDate()).padStart(2, '0');
        startDateTimeStr = `${year}-${month}-${day}T00:00:00`;
        
        // End date is the day after rentalEndDate (exclusive)
        const endDate = new Date(rentalEndDate);
        endDate.setDate(endDate.getDate() + 1);
        const endYear = endDate.getFullYear();
        const endMonth = String(endDate.getMonth() + 1).padStart(2, '0');
        const endDay = String(endDate.getDate()).padStart(2, '0');
        endDateTimeStr = `${endYear}-${endMonth}-${endDay}T00:00:00`;
      } else {
        // For appointments/events, use the slot's start time directly
        // The slot.start is already in local time format from backend (e.g., "2024-01-31T10:00:00")
        startDateTimeStr = selectedSlot.start;
        
        // Calculate end time by adding duration to start
        const [datePart, timePart] = selectedSlot.start.split('T');
        const [hours, minutes] = timePart.split(':').map(Number);
        const totalMinutes = hours * 60 + minutes + totalDuration;
        const endHours = Math.floor(totalMinutes / 60);
        const endMins = totalMinutes % 60;
        endDateTimeStr = `${datePart}T${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}:00`;
      }
      
      const bookingData = {
        calendar_id: selectedCalendar.calendar_id,
        service_ids: selectedServices.map(s => s.service_id),
        staff_id: selectedStaff?.staff_id || null,
        customer_name: customerInfo.name,
        customer_email: customerInfo.email,
        customer_phone: customerInfo.phone || null,
        start_datetime: startDateTimeStr,
        end_datetime: endDateTimeStr,
        notes: customerInfo.notes || null,
        num_attendees: customerInfo.num_attendees,
        num_seats: bookingType === "event" ? numSeats : 1,
        num_days: bookingType === "daily_rental" ? numDays : 1,
        fcm_token: fcmTokenFromUrl || null,  // FCM token for push notifications
        // Recurring booking fields
        is_recurring: isRecurring && business?.recurring_appointments_enabled,
        recurring_frequency: isRecurring ? recurringFrequency : null,
        recurring_count: isRecurring ? recurringCount : 1,
        // Discount code
        discount_code: discountResult ? discountCode : null
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      const booking = response.data;
      
      if (booking.payment_status === "pending" && booking.payment_amount > 0) {
        try {
          const paymentResponse = await axios.post(`${API}/payments/checkout?booking_id=${booking.booking_id}`, {}, {
            headers: { origin: window.location.origin }
          });
          window.location.href = paymentResponse.data.checkout_url;
        } catch (paymentError) {
          toast.error("Errore nel processare il pagamento");
          setSubmitting(false);
        }
      } else {
        // Always navigate to success page first
        // Pass redirect_url as parameter if available
        let successUrl = `/booking/success?booking_id=${booking.booking_id}`;
        if (booking.redirect_url) {
          // Ensure the redirect URL is absolute (has protocol)
          let redirectUrl = booking.redirect_url;
          if (!redirectUrl.startsWith('http://') && !redirectUrl.startsWith('https://')) {
            redirectUrl = 'https://' + redirectUrl;
          }
          successUrl += `&redirect_url=${encodeURIComponent(redirectUrl)}`;
        }
        navigate(successUrl);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la prenotazione");
      setSubmitting(false);
    }
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "S";
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="py-8 text-center">
            <p className="text-slate-500">Attività non trovata</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Dynamic theme color from business
  const themeColor = business?.background_color || "#4F46E5";
  
  // Convert hex to HSL for Tailwind CSS variable override
  const hexToHSL = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b), min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    if (max === min) { h = s = 0; } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        default: h = ((r - g) / d + 4) / 6; break;
      }
    }
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };
  const themeHSL = hexToHSL(themeColor);
  const currentLocale = dateLocales[i18n.language] || it;

  return (
    <div className="min-h-screen bg-slate-50 py-4 sm:py-8 px-2 sm:px-4">
      {/* Dynamic theme style */}
      <style>{`
        :root {
          --primary: ${themeHSL};
        }
        .theme-primary { background-color: ${themeColor}; }
        .theme-primary-text { color: ${themeColor}; }
        .theme-primary-border { border-color: ${themeColor}; }
        .theme-primary-bg-light { background-color: ${themeColor}15; }
        .theme-primary-ring { --tw-ring-color: ${themeColor}40; }
      `}</style>
      
      <div className="max-w-3xl mx-auto" data-testid="public-booking-page">
        {/* Language Selector */}
        <div className="flex justify-end mb-2">
          <LanguageSelector variant="ghost" showLabel={false} />
        </div>
        
        {/* Header with theme color accent */}
        <div className="text-center mb-4 sm:mb-8">
          <div 
            className="h-1 sm:h-1.5 w-16 sm:w-24 rounded-full mx-auto mb-3 sm:mb-6"
            style={{ backgroundColor: themeColor }}
          />
          {business.logo_url && (
            <img src={business.logo_url} alt={business.name} className="h-12 sm:h-16 mx-auto mb-2 sm:mb-4" />
          )}
          <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {business.name}
          </h1>
          {business.description && (
            <p className="text-sm sm:text-base text-slate-500 mt-1 sm:mt-2">{business.description}</p>
          )}
          {/* Show calendar name and/or staff if pre-selected from URL */}
          {calendarIdFromUrl && selectedCalendar && (
            <div className="mt-2 flex flex-wrap gap-2 justify-center">
              <div className="px-3 py-1 rounded-full text-sm font-medium inline-block"
                   style={{ backgroundColor: `${themeColor}20`, color: themeColor }}>
                📅 {selectedCalendar.name}
              </div>
              {staffPreselectedFromUrl && selectedStaff && (
                <div className="px-3 py-1 rounded-full text-sm font-medium inline-block"
                     style={{ backgroundColor: `${themeColor}20`, color: themeColor }}>
                  👤 {selectedStaff.name}
                </div>
              )}
            </div>
          )}
          {business.total_resources > 1 && (
            <p className="text-xs text-slate-400 mt-1">
              {t('booking.services.seats', { count: business.total_resources })}
            </p>
          )}
        </div>

        {/* Progress Steps with theme color */}
        <div className="flex items-center justify-center gap-1 sm:gap-2 mb-4 sm:mb-8">
          {getStepLabels().map((label, idx) => {
            const s = idx + 1;
            const displayStep = getDisplayStep();
            const totalSteps = getStepLabels().length;
            return (
              <div key={s} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className="w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs sm:text-sm font-medium transition-colors"
                    style={{
                      backgroundColor: displayStep >= s ? themeColor : "#E2E8F0",
                      color: displayStep >= s ? "white" : "#64748B"
                    }}
                  >
                    {displayStep > s ? <Check className="h-3 w-3 sm:h-4 sm:w-4" /> : s}
                  </div>
                  <span className="text-[10px] sm:text-xs text-slate-500 mt-1 hidden sm:block">{label}</span>
                </div>
                {s < totalSteps && (
                  <div 
                    className="w-4 sm:w-8 h-0.5 mx-0.5 sm:mx-1" 
                    style={{ backgroundColor: displayStep > s ? themeColor : "#E2E8F0" }}
                  />
                )}
              </div>
            );
          })}
        </div>

        {/* Cart Summary (always visible when services selected) */}
        {selectedServices.length > 0 && step > 1 && (
          <Card className="mb-6 border-primary/20 bg-primary/5">
            <CardContent className="py-3 sm:py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShoppingCart className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
                  <span className="text-sm sm:text-base font-medium">{selectedServices.length} servizi</span>
                </div>
                <div className="text-right">
                  <p className="font-bold text-primary">€{totalPrice.toFixed(2)}</p>
                  <p className="text-xs text-slate-500">{formatDuration(totalDuration)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 1: Dynamic based on booking_flow_order */}
        {step === 1 && bookingFlowOrder === "service_first" && (
          <Card className="border-slate-200 animate-fadeIn">
            <CardHeader className="pb-2 sm:pb-4">
              <CardTitle className="text-lg sm:text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>Scegli i Servizi</CardTitle>
              <CardDescription className="text-sm">Seleziona uno o più servizi</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {services.length === 0 ? (
                <p className="text-center text-slate-500 py-6">Nessun servizio disponibile</p>
              ) : (
                <>
                  {/* Mobile: Dropdown category filter */}
                  {categories.length > 1 && (
                    <div className="mb-4">
                      {/* Mobile: Select dropdown */}
                      <div className="block sm:hidden">
                        <Select 
                          value={selectedCategoryFilter || "all"} 
                          onValueChange={(v) => {
                            const catId = v === "all" ? null : v;
                            setSelectedCategoryFilter(catId);
                            // Auto-expand selected category
                            if (catId) {
                              setExpandedCategories(new Set([catId]));
                            } else {
                              // Expand all when "all" is selected
                              setExpandedCategories(new Set(categories.map(c => c.category_id)));
                            }
                          }}
                        >
                          <SelectTrigger className="w-full">
                            <div className="flex items-center gap-2">
                              <Grid3X3 className="h-4 w-4 text-slate-500" />
                              <SelectValue placeholder="Filtra per categoria" />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">
                              <span className="font-medium">Tutte le categorie</span>
                              <span className="text-slate-500 ml-2">({services.length})</span>
                            </SelectItem>
                            {categories.map((cat) => {
                              const catServiceCount = services.filter(s => s.category_id === cat.category_id).length;
                              return (
                                <SelectItem key={cat.category_id} value={cat.category_id}>
                                  <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color || '#6B7280' }}></div>
                                    <span>{cat.name}</span>
                                    <span className="text-slate-500">({catServiceCount})</span>
                                  </div>
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Desktop: Pills */}
                      <div className="hidden sm:block">
                        <div className="flex items-center gap-2 mb-2">
                          <Grid3X3 className="h-4 w-4 text-slate-500" />
                          <span className="text-sm font-medium text-slate-600">Filtra per categoria</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => {
                              setSelectedCategoryFilter(null);
                              setExpandedCategories(new Set(categories.map(c => c.category_id)));
                            }}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                              selectedCategoryFilter === null
                                ? "bg-primary text-white"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                            data-testid="category-filter-all"
                          >
                            Tutti
                          </button>
                          {categories.map((cat) => (
                            <button
                              key={cat.category_id}
                              onClick={() => {
                                setSelectedCategoryFilter(cat.category_id);
                                setExpandedCategories(new Set([cat.category_id]));
                              }}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                                selectedCategoryFilter === cat.category_id
                                  ? "text-white"
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }`}
                              style={{
                                backgroundColor: selectedCategoryFilter === cat.category_id ? cat.color : undefined
                              }}
                              data-testid={`category-filter-${cat.category_id}`}
                            >
                              {cat.image_url && (
                                <img src={cat.image_url} alt="" className="w-4 h-4 rounded-full object-cover" />
                              )}
                              {cat.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Services grouped by category - Accordion Style */}
                  <div className="space-y-3 mb-6">
                    {getFilteredServices().map(({ category, services: categoryServices }) => {
                      const isExpanded = expandedCategories.has(category.category_id);
                      const selectedInCategory = categoryServices.filter(s => 
                        selectedServices.some(sel => sel.service_id === s.service_id)
                      ).length;
                      
                      return (
                        <Collapsible 
                          key={category.category_id} 
                          open={isExpanded}
                          onOpenChange={() => toggleCategoryExpansion(category.category_id)}
                          className="border rounded-lg overflow-hidden"
                        >
                          {/* Category Header - Clickable */}
                          <CollapsibleTrigger className="w-full">
                            <div 
                              className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                              style={{ borderLeft: `4px solid ${category.color || '#6B7280'}` }}
                            >
                              <div className="flex items-center gap-2 sm:gap-3">
                                {category.image_url && (
                                  <img 
                                    src={category.image_url} 
                                    alt={category.name} 
                                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover"
                                  />
                                )}
                                <div className="text-left">
                                  <h3 className="font-semibold text-slate-800 text-sm sm:text-base">{category.name}</h3>
                                  <p className="text-xs text-slate-500">
                                    {categoryServices.length} servizi
                                    {selectedInCategory > 0 && (
                                      <span className="text-primary font-medium ml-1">
                                        • {selectedInCategory} selezionati
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {selectedInCategory > 0 && (
                                  <Badge 
                                    className="text-xs text-white"
                                    style={{ backgroundColor: category.color || '#6B7280' }}
                                  >
                                    {selectedInCategory}
                                  </Badge>
                                )}
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5 text-slate-400 transition-transform" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-slate-400 transition-transform" />
                                )}
                              </div>
                            </div>
                          </CollapsibleTrigger>

                          {/* Services in category - Collapsible Content */}
                          <CollapsibleContent>
                            <div className="divide-y divide-slate-100">
                              {categoryServices.map((service) => {
                                const isSelected = selectedServices.some(s => s.service_id === service.service_id);
                                return (
                                  <div
                                    key={service.service_id}
                                    className={`p-3 sm:p-4 transition-all cursor-pointer ${
                                      isSelected 
                                        ? "bg-primary/5" 
                                        : "hover:bg-slate-50"
                                    }`}
                                    onClick={() => toggleService(service)}
                                    data-testid={`service-option-${service.service_id}`}
                                  >
                                    <div className="flex items-start gap-3">
                                      <Checkbox 
                                        checked={isSelected}
                                        className="mt-0.5 flex-shrink-0 pointer-events-none"
                                      />
                                      {/* Service Image - smaller on mobile */}
                                      {service.image_url && (
                                        <div 
                                          className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 cursor-pointer"
                                          onClick={(e) => { e.stopPropagation(); setZoomedImage(service.image_url); }}
                                        >
                                          <img 
                                            src={service.image_url} 
                                            alt={service.name} 
                                            className="w-full h-full object-cover"
                                          />
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="min-w-0 flex-1">
                                            <h4 className="font-medium text-slate-900 text-sm sm:text-base">{service.name}</h4>
                                            {service.description && (
                                              <div className="mt-0.5">
                                                <p className={`text-xs text-slate-500 ${
                                                  expandedDescriptions.has(service.service_id) ? '' : 'line-clamp-2'
                                                }`}>
                                                  {service.description}
                                                </p>
                                                {service.description.length > 80 && (
                                                  <button
                                                    onClick={(e) => toggleDescription(service.service_id, e)}
                                                    className="text-xs text-primary font-medium mt-0.5 hover:underline"
                                                  >
                                                    {expandedDescriptions.has(service.service_id) ? 'mostra meno' : '...altro'}
                                                  </button>
                                                )}
                                              </div>
                                            )}
                                            <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500">
                                              <span className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {formatDuration(service.duration_minutes)}
                                              </span>
                                              {service.service_type === "event" && (
                                                <span className="text-blue-600">👥 {service.max_capacity}</span>
                                              )}
                                            </div>
                                          </div>
                                          <div className="text-right flex-shrink-0">
                                            <span className="font-bold text-primary text-sm sm:text-base">
                                              {service.service_type === "daily_rental" 
                                                ? `€${(service.price_per_day || service.price).toFixed(2)}/gg`
                                                : service.price > 0 ? `€${service.price.toFixed(2)}` : "Gratis"}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>

                  {/* Selected services summary + seat/day selection */}
                  {selectedServices.length > 0 && (
                    <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-4 mb-4 border border-primary/20">
                      <div className="flex items-center gap-2 mb-3">
                        <ShoppingCart className="h-5 w-5 text-primary" />
                        <h4 className="font-medium text-slate-900">Riepilogo Selezione</h4>
                      </div>
                      
                      {/* Multi-seat selection for events */}
                      {bookingType === "event" && (
                        <div className="bg-white rounded-lg p-4 mb-4 border border-blue-200">
                          <Label className="text-blue-800 font-medium mb-2 block">
                            👥 Quanti posti vuoi prenotare?
                          </Label>
                          <div className="flex items-center gap-4">
                            <Input
                              type="number"
                              min={selectedServices[0]?.min_seats || 1}
                              max={selectedServices[0]?.max_seats_per_booking || 10}
                              value={numSeats}
                              onChange={(e) => setNumSeats(Math.max(1, parseInt(e.target.value) || 1))}
                              className="w-24"
                            />
                            <span className="text-sm text-slate-500">
                              (min {selectedServices[0]?.min_seats || 1}, max {selectedServices[0]?.max_seats_per_booking || 10} posti)
                            </span>
                          </div>
                        </div>
                      )}
                      
                      {/* Multi-day selection for rentals - Calendar Range Picker */}
                      {bookingType === "daily_rental" && (
                        <div className="bg-white rounded-lg p-4 mb-4 border border-amber-200">
                          <Label className="text-amber-800 font-medium mb-3 block">
                            🏖️ Seleziona le date del noleggio
                          </Label>
                          
                          {/* Date Range Display */}
                          <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-amber-50 rounded-lg">
                            <div className="flex items-center gap-2">
                              <CalendarIcon className="h-4 w-4 text-amber-600" />
                              <span className="text-sm font-medium text-amber-800">
                                {rentalStartDate ? format(rentalStartDate, "dd MMM yyyy", { locale: dateLocales[i18n.language] || it }) : "Data inizio"}
                              </span>
                            </div>
                            <ArrowRight className="h-4 w-4 text-amber-400" />
                            <div className="flex items-center gap-2">
                              <CalendarIcon className="h-4 w-4 text-amber-600" />
                              <span className="text-sm font-medium text-amber-800">
                                {rentalEndDate ? format(rentalEndDate, "dd MMM yyyy", { locale: dateLocales[i18n.language] || it }) : "Data fine"}
                              </span>
                            </div>
                            {rentalStartDate && rentalEndDate && (
                              <Badge variant="secondary" className="bg-amber-100 text-amber-800 ml-auto">
                                {numDays} {numDays === 1 ? "giorno" : "giorni"}
                              </Badge>
                            )}
                          </div>
                          
                          {/* Calendar for Date Range Selection */}
                          <div className="flex justify-center">
                            <Calendar
                              mode="range"
                              selected={{ from: rentalStartDate, to: rentalEndDate }}
                              onSelect={(range) => {
                                if (range?.from) {
                                  setRentalStartDate(range.from);
                                  setSelectedDate(range.from);
                                  if (range.to) {
                                    setRentalEndDate(range.to);
                                    // Calculate number of days (inclusive)
                                    const diffTime = Math.abs(range.to - range.from);
                                    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1;
                                    setNumDays(diffDays);
                                  } else {
                                    setRentalEndDate(null);
                                    setNumDays(1);
                                  }
                                } else {
                                  setRentalStartDate(null);
                                  setRentalEndDate(null);
                                  setSelectedDate(null);
                                  setNumDays(1);
                                }
                              }}
                              numberOfMonths={1}
                              locale={dateLocales[i18n.language] || it}
                              disabled={(date) => {
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                if (date < today) return true;
                                // Check blocked dates
                                const yy = date.getFullYear();
                                const mm = String(date.getMonth() + 1).padStart(2, '0');
                                const dd = String(date.getDate()).padStart(2, '0');
                                const ds = `${yy}-${mm}-${dd}`;
                                return blockedDates.some(bd => ds >= bd.date_from && ds <= bd.date_to);
                              }}
                              className="rounded-md border"
                              classNames={{
                                day_range_middle: "bg-amber-100 text-amber-900",
                                day_selected: "bg-amber-500 text-white hover:bg-amber-600",
                                day_range_end: "bg-amber-500 text-white hover:bg-amber-600",
                              }}
                            />
                          </div>
                          
                          {/* Validation warnings */}
                          <div className="mt-3 space-y-2">
                            {rentalStartDate && rentalEndDate && numDays < (selectedServices[0]?.min_days || 1) && (
                              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                <span>Minimo {selectedServices[0]?.min_days || 1} giorni richiesti per questo servizio</span>
                              </div>
                            )}
                            {rentalStartDate && rentalEndDate && numDays > (selectedServices[0]?.max_days || 30) && (
                              <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-2 rounded">
                                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                                <span>Massimo {selectedServices[0]?.max_days || 30} giorni consentiti per questo servizio</span>
                              </div>
                            )}
                            {!rentalStartDate && (
                              <p className="text-sm text-slate-500 text-center">
                                Clicca su una data per selezionare l&apos;inizio del noleggio
                              </p>
                            )}
                            {rentalStartDate && !rentalEndDate && (
                              <p className="text-sm text-amber-600 text-center">
                                Ora seleziona la data di fine noleggio
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        {selectedServices.map((svc) => {
                          const cat = categories.find(c => c.category_id === svc.category_id);
                          const svcPrice = bookingType === "event" 
                            ? svc.price * numSeats 
                            : bookingType === "daily_rental" 
                              ? (svc.price_per_day || svc.price) * numDays 
                              : svc.price;
                          return (
                            <div key={svc.service_id} className="flex items-center justify-between text-sm bg-white/50 rounded p-2">
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                <div 
                                  className="w-2 h-2 rounded-full flex-shrink-0" 
                                  style={{ backgroundColor: svc.color || cat?.color || '#6B7280' }}
                                />
                                <span className="truncate">{svc.name}</span>
                                {bookingType === "event" && <span className="text-blue-600 flex-shrink-0">× {numSeats} posti</span>}
                                {bookingType === "daily_rental" && <span className="text-amber-600 flex-shrink-0">× {numDays} giorni</span>}
                                {bookingType === "appointment" && <span className="text-slate-400 flex-shrink-0">({formatDuration(svc.duration_minutes)})</span>}
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                                <span className="font-medium">€{svcPrice.toFixed(2)}</span>
                                <button 
                                  onClick={(e) => { e.stopPropagation(); removeService(svc.service_id); }}
                                  className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          );
                        })}
                        <div className="border-t border-primary/20 pt-3 mt-3 flex justify-between font-medium">
                          <span className="flex items-center gap-2">
                            <Clock className="h-4 w-4 text-primary" />
                            Durata totale: {formatDuration(totalDuration)}
                          </span>
                          <span className="text-primary text-lg">€{totalPrice.toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Validation check for rental days */}
                  {bookingType === "daily_rental" && rentalStartDate && rentalEndDate && (
                    numDays < (selectedServices[0]?.min_days || 1) || 
                    numDays > (selectedServices[0]?.max_days || 30)
                  ) && (
                    <p className="text-sm text-red-600 text-center mb-2">
                      Correggi il periodo selezionato per continuare
                    </p>
                  )}

                  <Button 
                    onClick={handleContinueToStaff} 
                    className="w-full"
                    disabled={
                      selectedServices.length === 0 || 
                      (bookingType === "daily_rental" && (
                        !rentalStartDate || 
                        !rentalEndDate ||
                        numDays < (selectedServices[0]?.min_days || 1) || 
                        numDays > (selectedServices[0]?.max_days || 30)
                      ))
                    }
                    data-testid="continue-to-staff-btn"
                  >
                    Continua
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 1: Staff First Flow - Select Staff */}
        {step === 1 && bookingFlowOrder === "staff_first" && (
          <Card className="border-slate-200 animate-fadeIn">
            <CardHeader className="pb-2 sm:pb-4">
              <CardTitle className="text-lg sm:text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>Scegli l&apos;Operatore</CardTitle>
              <CardDescription className="text-sm">Seleziona il professionista</CardDescription>
            </CardHeader>
            <CardContent className="pt-0">
              {allStaff.length === 0 ? (
                <div className="text-center py-6">
                  <Users className="h-10 w-10 sm:h-12 sm:w-12 text-slate-300 mx-auto mb-2" />
                  <p className="text-slate-500 text-sm">Nessun operatore disponibile</p>
                </div>
              ) : (
                <div className="space-y-2 sm:space-y-3">
                  {allStaff.filter(s => s.is_active !== false).map((staffMember) => {
                    // Get availability: prefer staff availability, fallback to calendar availability
                    const staffCalendar = calendars.find(c => c.staff_id === staffMember.staff_id);
                    const rawAvail = (staffMember.availability && Array.isArray(staffMember.availability) && staffMember.availability.length > 0) 
                      ? staffMember.availability 
                      : (staffCalendar?.default_availability || []);
                    const availabilityToShow = Array.isArray(rawAvail) ? rawAvail : [];
                    
                    // Detect if availability uses 0-based (0=Mon) or 1-based (1=Mon) indexing
                    const has0Index = availabilityToShow.some(a => a.day_of_week === 0);
                    const isOneBased = !has0Index && availabilityToShow.some(a => a.day_of_week === 1);
                    
                    return (
                      <div
                        key={staffMember.staff_id}
                        onClick={() => {
                          setSelectedStaff(staffMember);
                          setSelectedServices([]); // Reset services when staff changes
                          setSelectedCategoryFilter(null); // Reset category filter
                          // Auto-expand all categories
                          const staffSvcs = getStaffServiceIds(staffMember);
                          const staffCatIds = new Set(services.filter(s => staffSvcs.includes(s.service_id)).map(s => s.category_id).filter(Boolean));
                          setExpandedCategories(staffCatIds.size > 0 ? staffCatIds : new Set(categories.map(c => c.category_id)));
                          setStep(2);
                        }}
                        className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 rounded-xl border-2 cursor-pointer transition-all hover:border-primary hover:bg-primary/5 active:bg-primary/10"
                        data-testid={`staff-${staffMember.staff_id}`}
                      >
                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center flex-shrink-0">
                          {staffMember.avatar_url ? (
                            <img src={staffMember.avatar_url} alt={staffMember.name} className="h-10 w-10 sm:h-12 sm:w-12 rounded-full object-cover" />
                          ) : (
                            <User className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900">{staffMember.name}</p>
                          {staffMember.role && <p className="text-sm text-slate-500">{staffMember.role}</p>}
                          {staffMember.services?.length > 0 && (
                            <p className="text-xs text-slate-400 mt-1">
                              {staffMember.services.length} servizi disponibili
                            </p>
                          )}
                          {/* Show availability days - from staff or calendar */}
                          {availabilityToShow.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((dayName, idx) => {
                                // idx: 0=Lun, 1=Mar, ..., 6=Dom
                                // If 1-based: day_of_week 1=Lun, 2=Mar, ..., 6=Sab, 0 or 7=Dom
                                // If 0-based: day_of_week 0=Lun, 1=Mar, ..., 6=Dom
                                const backendDayOfWeek = isOneBased ? (idx === 6 ? 0 : idx + 1) : idx;
                                const dayAvail = availabilityToShow.find(a => a.day_of_week === backendDayOfWeek || (idx === 6 && a.day_of_week === 7));
                                // Consider available if is_available is true OR (is_available is null/undefined AND has periods)
                                const isAvailable = dayAvail && (dayAvail.is_available === true || (dayAvail.is_available !== false && dayAvail.periods?.length > 0));
                                return (
                                  <span 
                                    key={idx} 
                                    className={`text-xs px-1.5 py-0.5 rounded ${isAvailable ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}
                                  >
                                    {dayName}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <ArrowRight className="h-5 w-5 text-slate-400" />
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 2: Dynamic based on booking_flow_order */}
        {/* Service First Flow: Select Staff */}
        {step === 2 && bookingFlowOrder === "service_first" && (
          <Card className="border-slate-200 animate-fadeIn">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => setStep(1)} data-testid="back-to-services">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Scegli il Professionista</CardTitle>
                  <CardDescription>Seleziona chi preferisci (opzionale)</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {staff.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-slate-500 mb-4">Nessun professionista specifico richiesto</p>
                  <Button onClick={handleSkipStaff} data-testid="skip-staff-btn">
                    Continua <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {staff.map((member) => {
                    // Get availability: prefer staff availability, fallback to calendar availability
                    const staffCalendar = calendars.find(c => c.staff_id === member.staff_id);
                    const rawAvail2 = (member.availability && Array.isArray(member.availability) && member.availability.length > 0) 
                      ? member.availability 
                      : (staffCalendar?.default_availability || []);
                    const availabilityToShow = Array.isArray(rawAvail2) ? rawAvail2 : [];
                    
                    // Detect if availability uses 0-based (0=Mon) or 1-based (1=Mon) indexing
                    const has0Index = availabilityToShow.some(a => a.day_of_week === 0);
                    const has7Index = availabilityToShow.some(a => a.day_of_week === 7);
                    // If has day 0 and day 6, it's 0-based. If has day 1 and day 6/7, it's 1-based
                    const isOneBased = !has0Index && availabilityToShow.some(a => a.day_of_week === 1);
                    
                    return (
                      <button
                        key={member.staff_id}
                        onClick={() => handleStaffSelect(member)}
                        className="w-full text-left p-4 rounded-lg border border-slate-200 hover:border-primary hover:bg-primary/5 transition-all"
                        data-testid={`staff-option-${member.staff_id}`}
                      >
                        <div className="flex items-center gap-4">
                          <Avatar className="h-12 w-12">
                            <AvatarImage src={member.photo_url} />
                            <AvatarFallback className="bg-primary/10 text-primary">
                              {getInitials(member.name)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <h3 className="font-medium text-slate-900">{member.name}</h3>
                            {member.role && <p className="text-sm text-slate-500">{member.role}</p>}
                            {/* Show availability days - from staff or calendar */}
                            {availabilityToShow.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((dayName, idx) => {
                                  // idx: 0=Lun, 1=Mar, ..., 6=Dom
                                  // If 1-based: day_of_week 1=Lun, 2=Mar, ..., 6=Sab, 0 or 7=Dom
                                  // If 0-based: day_of_week 0=Lun, 1=Mar, ..., 6=Dom
                                  const backendDayOfWeek = isOneBased ? (idx === 6 ? 0 : idx + 1) : idx;
                                  const dayAvail = availabilityToShow.find(a => a.day_of_week === backendDayOfWeek || (idx === 6 && a.day_of_week === 7));
                                  // Consider available if is_available is true OR (is_available is null/undefined AND has periods)
                                  const isAvailable = dayAvail && (dayAvail.is_available === true || (dayAvail.is_available !== false && dayAvail.periods?.length > 0));
                                  return (
                                    <span 
                                      key={idx} 
                                      className={`text-xs px-1.5 py-0.5 rounded ${isAvailable ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-400'}`}
                                    >
                                      {dayName}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  <Button variant="outline" className="w-full mt-4" onClick={handleSkipStaff} data-testid="any-staff-btn">
                    Nessuna preferenza
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Staff First Flow: Step 2 - Select Services (filtered by selected staff, grouped by category) */}
        {step === 2 && bookingFlowOrder === "staff_first" && (
          <Card className="border-slate-200 animate-fadeIn">
            <CardHeader className="pb-2 sm:pb-4">
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setStep(1); setSelectedStaff(null); }} data-testid="back-to-staff">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                  <CardTitle className="text-lg sm:text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>Scegli i Servizi</CardTitle>
                  <CardDescription className="text-sm">
                    Servizi di {selectedStaff?.name || "operatore"}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {getFilteredServicesForStaff().length === 0 ? (
                <p className="text-center text-slate-500 py-6 text-sm">Nessun servizio disponibile per questo operatore</p>
              ) : (
                <>
                  {/* Category filter - only show if more than 1 category */}
                  {getServicesForStaffByCategory().length > 1 && (
                    <div className="mb-4">
                      {/* Mobile: Select dropdown */}
                      <div className="block sm:hidden">
                        <Select 
                          value={selectedCategoryFilter || "all"} 
                          onValueChange={(v) => {
                            const catId = v === "all" ? null : v;
                            setSelectedCategoryFilter(catId);
                            if (catId) {
                              setExpandedCategories(new Set([catId]));
                            } else {
                              setExpandedCategories(new Set(getServicesForStaffByCategory().map(g => g.category.category_id)));
                            }
                          }}
                        >
                          <SelectTrigger className="w-full" data-testid="staff-first-category-filter-mobile">
                            <div className="flex items-center gap-2">
                              <Grid3X3 className="h-4 w-4 text-slate-500" />
                              <SelectValue placeholder="Filtra per categoria" />
                            </div>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">
                              <span className="font-medium">Tutte le categorie</span>
                            </SelectItem>
                            {getServicesForStaffByCategory().map(({ category, services: catServices }) => (
                              <SelectItem key={category.category_id} value={category.category_id}>
                                <div className="flex items-center gap-2">
                                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: category.color || '#6B7280' }}></div>
                                  <span>{category.name}</span>
                                  <span className="text-slate-500">({catServices.length})</span>
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Desktop: Pills */}
                      <div className="hidden sm:block">
                        <div className="flex items-center gap-2 mb-2">
                          <Grid3X3 className="h-4 w-4 text-slate-500" />
                          <span className="text-sm font-medium text-slate-600">Filtra per categoria</span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => {
                              setSelectedCategoryFilter(null);
                              setExpandedCategories(new Set(getServicesForStaffByCategory().map(g => g.category.category_id)));
                            }}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                              selectedCategoryFilter === null
                                ? "bg-primary text-white"
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                            data-testid="staff-first-category-filter-all"
                          >
                            Tutti
                          </button>
                          {getServicesForStaffByCategory().map(({ category }) => (
                            <button
                              key={category.category_id}
                              onClick={() => {
                                setSelectedCategoryFilter(category.category_id);
                                setExpandedCategories(new Set([category.category_id]));
                              }}
                              className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                                selectedCategoryFilter === category.category_id
                                  ? "text-white"
                                  : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }`}
                              style={{
                                backgroundColor: selectedCategoryFilter === category.category_id ? category.color : undefined
                              }}
                              data-testid={`staff-first-category-filter-${category.category_id}`}
                            >
                              {category.image_url && (
                                <img src={category.image_url} alt="" className="w-4 h-4 rounded-full object-cover" />
                              )}
                              {category.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Services grouped by category - Accordion Style */}
                  <div className="space-y-3 mb-6">
                    {getFilteredServicesForStaff().map(({ category, services: categoryServices }) => {
                      const isExpanded = expandedCategories.has(category.category_id);
                      const selectedInCategory = categoryServices.filter(s => 
                        selectedServices.some(sel => sel.service_id === s.service_id)
                      ).length;
                      
                      return (
                        <Collapsible 
                          key={category.category_id} 
                          open={isExpanded}
                          onOpenChange={() => toggleCategoryExpansion(category.category_id)}
                          className="border rounded-lg overflow-hidden"
                        >
                          <CollapsibleTrigger className="w-full" data-testid={`staff-first-category-${category.category_id}`}>
                            <div 
                              className="flex items-center justify-between p-3 sm:p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                              style={{ borderLeft: `4px solid ${category.color || '#6B7280'}` }}
                            >
                              <div className="flex items-center gap-2 sm:gap-3">
                                {category.image_url && (
                                  <img 
                                    src={category.image_url} 
                                    alt={category.name} 
                                    className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg object-cover"
                                  />
                                )}
                                <div className="text-left">
                                  <h3 className="font-semibold text-slate-800 text-sm sm:text-base">{category.name}</h3>
                                  <p className="text-xs text-slate-500">
                                    {categoryServices.length} servizi
                                    {selectedInCategory > 0 && (
                                      <span className="text-primary font-medium ml-1">
                                        • {selectedInCategory} selezionati
                                      </span>
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {selectedInCategory > 0 && (
                                  <Badge 
                                    className="text-xs text-white"
                                    style={{ backgroundColor: category.color || '#6B7280' }}
                                  >
                                    {selectedInCategory}
                                  </Badge>
                                )}
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5 text-slate-400 transition-transform" />
                                ) : (
                                  <ChevronRight className="h-5 w-5 text-slate-400 transition-transform" />
                                )}
                              </div>
                            </div>
                          </CollapsibleTrigger>

                          <CollapsibleContent>
                            <div className="divide-y divide-slate-100">
                              {categoryServices.map((service) => {
                                const isSelected = selectedServices.some(s => s.service_id === service.service_id);
                                return (
                                  <div
                                    key={service.service_id}
                                    className={`p-3 sm:p-4 transition-all cursor-pointer ${
                                      isSelected 
                                        ? "bg-primary/5" 
                                        : "hover:bg-slate-50"
                                    }`}
                                    onClick={() => toggleService(service)}
                                    data-testid={`staff-first-service-${service.service_id}`}
                                  >
                                    <div className="flex items-start gap-3">
                                      <Checkbox 
                                        checked={isSelected}
                                        className="mt-0.5 flex-shrink-0 pointer-events-none"
                                      />
                                      {service.image_url && (
                                        <div 
                                          className="w-12 h-12 sm:w-16 sm:h-16 rounded-lg overflow-hidden flex-shrink-0 bg-slate-100 cursor-pointer"
                                          onClick={(e) => { e.stopPropagation(); setZoomedImage(service.image_url); }}
                                        >
                                          <img 
                                            src={service.image_url} 
                                            alt={service.name} 
                                            className="w-full h-full object-cover"
                                          />
                                        </div>
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <div className="flex items-start justify-between gap-2">
                                          <div className="min-w-0 flex-1">
                                            <h4 className="font-medium text-slate-900 text-sm sm:text-base">{service.name}</h4>
                                            {service.description && (
                                              <div className="mt-0.5">
                                                <p className={`text-xs text-slate-500 ${
                                                  expandedDescriptions.has(service.service_id) ? '' : 'line-clamp-2'
                                                }`}>
                                                  {service.description}
                                                </p>
                                                {service.description.length > 80 && (
                                                  <button
                                                    onClick={(e) => toggleDescription(service.service_id, e)}
                                                    className="text-xs text-primary font-medium mt-0.5 hover:underline"
                                                  >
                                                    {expandedDescriptions.has(service.service_id) ? 'mostra meno' : '...altro'}
                                                  </button>
                                                )}
                                              </div>
                                            )}
                                            <div className="flex items-center gap-2 mt-1.5 text-xs text-slate-500">
                                              <span className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {formatDuration(service.duration_minutes)}
                                              </span>
                                            </div>
                                          </div>
                                          <div className="text-right flex-shrink-0">
                                            <span className="font-bold text-primary text-sm sm:text-base">
                                              {service.price > 0 ? `€${service.price.toFixed(2)}` : "Gratis"}
                                            </span>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </CollapsibleContent>
                        </Collapsible>
                      );
                    })}
                  </div>
                  
                  {/* Selected Services Summary */}
                  {selectedServices.length > 0 && (
                    <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg p-4 mb-4 border border-primary/20">
                      <div className="flex items-center gap-2 mb-3">
                        <ShoppingCart className="h-5 w-5 text-primary" />
                        <h4 className="font-medium text-slate-900">Riepilogo Selezione</h4>
                      </div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Selezionati: {selectedServices.length}</span>
                        <span className="text-sm text-slate-500">{totalDuration} min totali</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="font-medium">Totale:</span>
                        <span className="text-lg font-bold text-primary">€{totalPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  )}
                  
                  <Button
                    className="w-full"
                    disabled={selectedServices.length === 0}
                    onClick={() => {
                      const staffCalendar = calendars.find(c => c.staff_id === selectedStaff?.staff_id);
                      setSelectedCalendar(staffCalendar || calendars[0]);
                      setStep(3);
                    }}
                    style={{ backgroundColor: themeColor }}
                    data-testid="continue-to-datetime"
                  >
                    Continua
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Step 3: Select Date & Time */}
        {step === 3 && (
          <Card className="border-slate-200 animate-fadeIn">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => setStep(2)} data-testid="back-to-step2">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>Scegli Data e Orario</CardTitle>
                  <CardDescription>
                    Durata totale richiesta: {formatDuration(totalDuration)}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                {/* Calendar */}
                <div className="flex justify-center">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={(date) => {
                      const today = new Date();
                      today.setHours(0, 0, 0, 0);
                      const maxDate = new Date();
                      maxDate.setDate(maxDate.getDate() + (selectedCalendar?.max_days_advance || 180));
                      
                      // Check if date is within range
                      if (date < today || date > maxDate) return true;
                      
                      // Check business-wide blocked dates
                      const yy = date.getFullYear();
                      const mm = String(date.getMonth() + 1).padStart(2, '0');
                      const dd = String(date.getDate()).padStart(2, '0');
                      const dateStr = `${yy}-${mm}-${dd}`;
                      const isBlocked = blockedDates.some(bd => dateStr >= bd.date_from && dateStr <= bd.date_to);
                      if (isBlocked) return true;
                      
                      // JavaScript: 0=Sunday, 1=Monday, 2=Tuesday, ..., 6=Saturday
                      const jsDayOfWeek = date.getDay();
                      
                      // Check calendar availability
                      const calendarAvailability = selectedCalendar?.default_availability || [];
                      
                      // Detect if calendar uses 0-based (0=Mon) or 1-based (1=Mon) indexing
                      const calHas0Index = calendarAvailability.some(a => a.day_of_week === 0);
                      const calIsOneBased = !calHas0Index && calendarAvailability.some(a => a.day_of_week === 1);
                      
                      // Convert JS day to backend format
                      // JS: 0=Sun, 1=Mon, ..., 6=Sat
                      // Backend 0-based: 0=Mon, 1=Tue, ..., 5=Sat, 6=Sun
                      // Backend 1-based: 1=Mon, 2=Tue, ..., 6=Sat, 0 or 7=Sun
                      let backendDayOfWeek;
                      if (calIsOneBased) {
                        // 1-based: JS Sun(0)->0 or 7, JS Mon(1)->1, ..., JS Sat(6)->6
                        backendDayOfWeek = jsDayOfWeek === 0 ? 0 : jsDayOfWeek;
                      } else {
                        // 0-based: JS Sun(0)->6, JS Mon(1)->0, ..., JS Sat(6)->5
                        backendDayOfWeek = (jsDayOfWeek + 6) % 7;
                      }
                      
                      const calendarDayAvail = calendarAvailability.find(a => 
                        a.day_of_week === backendDayOfWeek || (jsDayOfWeek === 0 && a.day_of_week === 7)
                      );
                      
                      // Disable if calendar not available
                      if (!calendarDayAvail) return true;
                      if (calendarDayAvail.is_available === false) return true;
                      if (!calendarDayAvail.periods || calendarDayAvail.periods.length === 0) return true;
                      
                      // If staff is selected, also check staff availability
                      if (selectedStaff) {
                        const staffAvailability = selectedStaff.availability || [];
                        if (staffAvailability.length > 0) {
                          // Detect staff's indexing format
                          const staffHas0Index = staffAvailability.some(a => a.day_of_week === 0);
                          const staffIsOneBased = !staffHas0Index && staffAvailability.some(a => a.day_of_week === 1);
                          
                          let staffBackendDay;
                          if (staffIsOneBased) {
                            staffBackendDay = jsDayOfWeek === 0 ? 0 : jsDayOfWeek;
                          } else {
                            staffBackendDay = (jsDayOfWeek + 6) % 7;
                          }
                          
                          const staffDayAvail = staffAvailability.find(a => 
                            a.day_of_week === staffBackendDay || (jsDayOfWeek === 0 && a.day_of_week === 7)
                          );
                          
                          if (!staffDayAvail) return true;
                          if (staffDayAvail.is_available === false) return true;
                          if (!staffDayAvail.periods || staffDayAvail.periods.length === 0) return true;
                        }
                      }
                      
                      return false;
                    }}
                    modifiers={{
                      available: (date) => {
                        const today = new Date();
                        today.setHours(0, 0, 0, 0);
                        const maxDate = new Date();
                        maxDate.setDate(maxDate.getDate() + (selectedCalendar?.max_days_advance || 180));
                        
                        if (date < today || date > maxDate) return false;
                        
                        // Check blocked dates
                        const yy = date.getFullYear();
                        const mm = String(date.getMonth() + 1).padStart(2, '0');
                        const dd = String(date.getDate()).padStart(2, '0');
                        const ds = `${yy}-${mm}-${dd}`;
                        if (blockedDates.some(bd => ds >= bd.date_from && ds <= bd.date_to)) return false;
                        
                        const jsDayOfWeek = date.getDay();
                        
                        const calendarAvailability = selectedCalendar?.default_availability || [];
                        const calHas0Index = calendarAvailability.some(a => a.day_of_week === 0);
                        const calIsOneBased = !calHas0Index && calendarAvailability.some(a => a.day_of_week === 1);
                        
                        let backendDayOfWeek;
                        if (calIsOneBased) {
                          backendDayOfWeek = jsDayOfWeek === 0 ? 0 : jsDayOfWeek;
                        } else {
                          backendDayOfWeek = (jsDayOfWeek + 6) % 7;
                        }
                        
                        const calendarDayAvail = calendarAvailability.find(a => 
                          a.day_of_week === backendDayOfWeek || (jsDayOfWeek === 0 && a.day_of_week === 7)
                        );
                        
                        if (!calendarDayAvail) return false;
                        if (calendarDayAvail.is_available === false) return false;
                        if (!calendarDayAvail.periods || calendarDayAvail.periods.length === 0) return false;
                        
                        if (selectedStaff) {
                          const staffAvailability = selectedStaff.availability || [];
                          if (staffAvailability.length > 0) {
                            const staffHas0Index = staffAvailability.some(a => a.day_of_week === 0);
                            const staffIsOneBased = !staffHas0Index && staffAvailability.some(a => a.day_of_week === 1);
                            
                            let staffBackendDay;
                            if (staffIsOneBased) {
                              staffBackendDay = jsDayOfWeek === 0 ? 0 : jsDayOfWeek;
                            } else {
                              staffBackendDay = (jsDayOfWeek + 6) % 7;
                            }
                            
                            const staffDayAvail = staffAvailability.find(a => 
                              a.day_of_week === staffBackendDay || (jsDayOfWeek === 0 && a.day_of_week === 7)
                            );
                            
                            if (!staffDayAvail) return false;
                            if (staffDayAvail.is_available === false) return false;
                            if (!staffDayAvail.periods || staffDayAvail.periods.length === 0) return false;
                          }
                        }
                        
                        return true;
                      }
                    }}
                    modifiersStyles={{
                      available: {
                        backgroundColor: business?.background_color ? `${business.background_color}20` : '#4F46E520',
                        color: business?.background_color || '#4F46E5',
                        fontWeight: '600',
                        border: `2px solid ${business?.background_color || '#4F46E5'}`
                      }
                    }}
                    locale={dateLocales[i18n.language?.split("-")[0]] || it}
                    className="rounded-md border"
                    data-testid="booking-calendar"
                  />
                </div>

                {/* Time Slots */}
                <div>
                  <h4 className="font-medium text-slate-900 mb-3">
                    {selectedDate ? format(selectedDate, "EEEE d MMMM", { locale: it }) : "Seleziona una data"}
                  </h4>
                  
                  {!selectedDate ? (
                    <p className="text-slate-500 text-sm">Scegli una data dal calendario</p>
                  ) : loadingSlots ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-primary" />
                    </div>
                  ) : slots.length === 0 ? (
                    <p className="text-slate-500 text-sm">Nessuno slot disponibile per questa data e durata ({totalDuration} min)</p>
                  ) : (
                    <div className="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                      {slots.map((slot, index) => (
                        <button
                          key={index}
                          onClick={() => handleSlotSelect(slot)}
                          className={`p-2 text-sm rounded-lg border transition-all ${
                            selectedSlot?.start === slot.start
                              ? "border-primary bg-primary/10 text-primary"
                              : "border-slate-200 hover:border-primary hover:bg-primary/5"
                          }`}
                          data-testid={`slot-${slot.display}`}
                        >
                          <span>{slot.display}</span>
                          {slot.resources_available && business.total_resources > 1 && (
                            <span className="block text-xs text-slate-400">
                              {slot.resources_available} posti
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 4: Customer Details */}
        {step === 4 && (
          <Card className="border-slate-200 animate-fadeIn">
            <CardHeader>
              <div className="flex items-center gap-2">
                <Button variant="ghost" size="icon" onClick={() => setStep(3)} data-testid="back-to-datetime">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <div>
                  <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>I Tuoi Dati</CardTitle>
                  <CardDescription>Inserisci le tue informazioni di contatto</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Summary */}
              <div className="bg-slate-50 rounded-lg p-4 mb-6">
                <h4 className="font-medium text-slate-900 mb-2">Riepilogo Prenotazione</h4>
                <div className="space-y-1 text-sm">
                  <p><span className="text-slate-500">Servizi:</span></p>
                  <ul className="ml-4 list-disc">
                    {selectedServices.map(svc => (
                      <li key={svc.service_id}>{svc.name} - {formatDuration(svc.duration_minutes)} - €{svc.price.toFixed(2)}</li>
                    ))}
                  </ul>
                  {selectedStaff && <p><span className="text-slate-500">Con:</span> {selectedStaff.name}</p>}
                  
                  {/* Date display based on booking type */}
                  {bookingType === "daily_rental" ? (
                    <>
                      <p><span className="text-slate-500">Data Inizio:</span> {selectedDate && format(selectedDate, "EEEE d MMMM yyyy", { locale: it })}</p>
                      <p><span className="text-slate-500">Data Fine:</span> {selectedDate && format(addDays(selectedDate, numDays - 1), "EEEE d MMMM yyyy", { locale: it })}</p>
                      <p><span className="text-slate-500">Durata:</span> {numDays} giorni</p>
                    </>
                  ) : (
                    <>
                      <p><span className="text-slate-500">Data:</span> {selectedDate && format(selectedDate, "EEEE d MMMM yyyy", { locale: it })}</p>
                      <p><span className="text-slate-500">Orario:</span> {selectedSlot?.display}</p>
                      <p><span className="text-slate-500">Durata totale:</span> {formatDuration(totalDuration)}</p>
                    </>
                  )}
                  
                  <p className="font-medium pt-2 border-t mt-2">
                    <span className="text-slate-500">Prezzo totale:</span> {totalPrice > 0 ? `€${totalPrice.toFixed(2)}` : "Gratuito"}
                  </p>
                </div>
              </div>

              {/* Recurring Booking Option */}
              {business?.recurring_appointments_enabled && bookingType === "appointment" && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center gap-3 mb-3">
                    <Checkbox
                      id="recurring"
                      checked={isRecurring}
                      onCheckedChange={setIsRecurring}
                    />
                    <Label htmlFor="recurring" className="font-medium text-blue-900 cursor-pointer">
                      Prenota appuntamento ricorrente
                    </Label>
                  </div>
                  
                  {isRecurring && (
                    <div className="ml-6 space-y-3">
                      <div>
                        <Label className="text-sm text-blue-800">Frequenza</Label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {[
                            { value: "weekly", label: "Settimanale" },
                            { value: "biweekly", label: "Ogni 2 settimane" },
                            { value: "monthly", label: "Mensile" }
                          ].map(freq => (
                            <Button
                              key={freq.value}
                              type="button"
                              variant={recurringFrequency === freq.value ? "default" : "outline"}
                              size="sm"
                              onClick={() => setRecurringFrequency(freq.value)}
                              className="text-xs"
                            >
                              {freq.label}
                            </Button>
                          ))}
                        </div>
                      </div>
                      
                      <div>
                        <Label className="text-sm text-blue-800">Numero di appuntamenti</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setRecurringCount(Math.max(2, recurringCount - 1))}
                          >
                            -
                          </Button>
                          <span className="w-8 text-center font-medium">{recurringCount}</span>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setRecurringCount(Math.min(12, recurringCount + 1))}
                          >
                            +
                          </Button>
                          <span className="text-xs text-blue-700 ml-2">
                            (totale {recurringCount} appuntamenti)
                          </span>
                        </div>
                      </div>
                      
                      <p className="text-xs text-blue-600 mt-2">
                        Verranno prenotati {recurringCount} appuntamenti: il primo in data selezionata, 
                        poi ogni {recurringFrequency === "weekly" ? "settimana" : recurringFrequency === "biweekly" ? "2 settimane" : "mese"}.
                      </p>
                    </div>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome Completo *</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="name"
                      value={customerInfo.name}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, name: e.target.value })}
                      placeholder="Mario Rossi"
                      className="pl-10"
                      required
                      data-testid="customer-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      value={customerInfo.email}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, email: e.target.value })}
                      placeholder="mario@esempio.com"
                      className="pl-10"
                      required
                      data-testid="customer-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="phone"
                      value={customerInfo.phone}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, phone: e.target.value })}
                      placeholder="+39 123 456 7890"
                      className="pl-10"
                      data-testid="customer-phone-input"
                    />
                  </div>
                </div>

                {business.total_resources > 1 && (
                  <div className="space-y-2">
                    <Label htmlFor="attendees">Numero di Partecipanti</Label>
                    <Input
                      id="attendees"
                      type="number"
                      min="1"
                      max={business.total_resources}
                      value={customerInfo.num_attendees}
                      onChange={(e) => setCustomerInfo({ ...customerInfo, num_attendees: parseInt(e.target.value) })}
                      data-testid="customer-attendees-input"
                    />
                    <p className="text-xs text-slate-500">Max {business.total_resources} posti disponibili</p>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="notes">Note (opzionale)</Label>
                  <Textarea
                    id="notes"
                    value={customerInfo.notes}
                    onChange={(e) => setCustomerInfo({ ...customerInfo, notes: e.target.value })}
                    placeholder="Informazioni aggiuntive..."
                    rows={3}
                    data-testid="customer-notes-input"
                  />
                </div>

                {/* Discount Code */}
                {totalPrice > 0 && (
                  <div className="space-y-2 p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg border border-amber-200" data-testid="discount-section">
                    <Label className="text-sm font-medium flex items-center gap-2">
                      <Tag className="h-4 w-4 text-amber-600" />
                      Codice Sconto
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={discountCode}
                        onChange={(e) => { setDiscountCode(e.target.value.toUpperCase()); setDiscountResult(null); }}
                        placeholder="Inserisci codice..."
                        className="font-mono"
                        data-testid="discount-code-input"
                      />
                      <Button type="button" variant="outline" size="sm" onClick={validateDiscount} disabled={validatingDiscount || !discountCode.trim()} data-testid="apply-discount-btn">
                        {validatingDiscount ? <Loader2 className="h-4 w-4 animate-spin" /> : "Applica"}
                      </Button>
                    </div>
                    {discountResult && (
                      <div className="flex items-center justify-between p-2 bg-green-50 border border-green-200 rounded-lg text-sm" data-testid="discount-applied">
                        <div className="flex items-center gap-2 text-green-700">
                          <CheckCircle className="h-4 w-4" />
                          <span className="font-medium">{discountResult.code}</span>
                          <span>({discountResult.discount_type === 'percentage' ? `${discountResult.discount_value}%` : `€${discountResult.discount_value}`})</span>
                        </div>
                        <div className="text-right">
                          <span className="text-green-700 font-bold">-€{discountResult.discount_amount.toFixed(2)}</span>
                          <div className="text-xs text-slate-500">Totale: €{discountResult.final_amount.toFixed(2)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <Button type="submit" className="w-full" disabled={submitting} data-testid="submit-booking-btn">
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Elaborazione...
                    </>
                  ) : business?.payment_mode === "full" && totalPrice > 0 ? (
                    <>
                      Procedi al Pagamento
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  ) : business?.payment_mode === "percentage" && totalPrice > 0 ? (
                    <>
                      Completa la Prenotazione e Paga l'Acconto
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  ) : (
                    <>
                      Completa la Prenotazione
                      <Check className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Image Zoom Modal */}
      {zoomedImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4 cursor-pointer"
          onClick={() => setZoomedImage(null)}
          data-testid="image-zoom-modal"
        >
          <img 
            src={zoomedImage} 
            alt="" 
            className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
          <button 
            className="absolute top-4 right-4 text-white bg-black/50 rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 transition-colors"
            onClick={() => setZoomedImage(null)}
          >
            &times;
          </button>
        </div>
      )}
    </div>
  );
};

export default PublicBooking;
